import type { Track, BeatNotifier } from "./types";

import { Sequence } from "tone/build/esm/event/Sequence.js";
import { ToneAudioBuffers } from "tone/build/esm/core/context/ToneAudioBuffers.js";
import { getContext } from "tone/build/esm/core/Global.js";

const NB_BEATS = 16;

declare const process: { env: { PUBLIC_URL?: string } };

function sampleUrl(name: string): string {
  return `${process.env.PUBLIC_URL || ""}/audio/${name}.wav`;
}

let buffers: ToneAudioBuffers | null = null;
let master: GainNode | null = null;

let pendingLoads = 0;
let waitPromise: Promise<void> | null = null;
let resolveWait: (() => void) | null = null;

const velocities = [
  1, .5, .75, .5,
  1, .5, .75, .5,
  1, .5, .75, .5,
  1, .5, .75, .5,
];

function onBufferLoaded(): void {
  pendingLoads--;
  if (pendingLoads === 0 && resolveWait) {
    const resolve = resolveWait;
    resolveWait = null;
    waitPromise = null;
    resolve();
  }
}

function getMaster(): GainNode {
  if (!master) {
    const ctx = getContext();
    master = ctx.createGain();
    master.gain.value = 1;
    master.connect(ctx.rawContext.destination);
  }
  return master;
}

export function loadTracks(tracks: Track[]): void {
  if (!buffers) {
    buffers = new ToneAudioBuffers();
  }
  for (const {name} of tracks) {
    if (!buffers.has(name)) {
      pendingLoads++;
      buffers.add(name, sampleUrl(name), onBufferLoaded);
    }
  }
}

export function waitForBuffers(): Promise<void> {
  if (pendingLoads === 0) {
    return Promise.resolve();
  }
  if (!waitPromise) {
    waitPromise = new Promise<void>((resolve) => {
      resolveWait = resolve;
    });
  }
  return waitPromise;
}

export function create(tracks: Track[], beatNotifier: BeatNotifier): Sequence {
  const loop = new Sequence(
    loopProcessor(tracks, beatNotifier),
    new Array(NB_BEATS).fill(0).map((_, i) => i),
    "16n"
  );

  return loop;
}

export function startTransport(): void {
  getContext().transport.start();
}

export function update(loop: Sequence, tracks: Track[], beatNotifier: BeatNotifier): Sequence {
  loop.callback = loopProcessor(tracks, beatNotifier);
  return loop;
}

export function updateBPM(bpm: number): void {
  getContext().transport.bpm.value = bpm;
}

function loopProcessor(tracks: Track[], beatNotifier: BeatNotifier) {
  const output = getMaster();

  return (time: number, index: number) => {
    if (!buffers) {
      return;
    }
    beatNotifier(index);
    tracks.forEach(({name, vol, muted, beats}) => {
      if (!beats[index]) {
        return;
      }
      const toneBuffer = buffers!.get(name);
      if (!toneBuffer || !toneBuffer.loaded) {
        return;
      }
      const audioBuffer = toneBuffer.get();
      if (!audioBuffer) {
        return;
      }
      const velocity = muted ? 0 : velocities[index] * vol;
      try {
        const ctx = getContext();
        const source = ctx.createBufferSource();
        source.buffer = audioBuffer;
        const gain = ctx.createGain();
        gain.gain.value = velocity;
        source.connect(gain);
        gain.connect(output);
        const bpm = ctx.transport.bpm.value;
        source.start(time, 0, 4 * (60 / bpm));
      } catch(e) {
        console.warn(`[sequencer] start failed for ${name}:`, (e as Error).message);
      }
    });
  };
}